import { Results } from "types/types";

/**
 * Normalize input and convert letters to indices
 */
function lettersToIndices(letters: string): number[] {
  letters = letters.replace(/\s+/g, '');
  letters = letters.toUpperCase();
  return letters.split(',').map((letter) => letter.charCodeAt(0) - 'A'.charCodeAt(0));
}

/**
 * Function to filter included columns based on user input.
 * If no columns are specified, all original columns will be included.
 * @param originalResults Original results from parsed CSV file
 * @param includedColumns String of included columns (e.g. 'A,C,E')
 * @returns filtered Results object
 */
export function filterIncludedColumns(originalResults: Results, includedColumns: string): Results {
  if(includedColumns === '') return originalResults;

  const includedColumnIndices = lettersToIndices(includedColumns);

  const filteredData = originalResults.data.map((row) =>
    Object.fromEntries(
      Object.entries(row).filter((_, index) => includedColumnIndices.includes(index)),
    ),
  );

  const filteredFields = originalResults.fields.filter((_, index) =>
    includedColumnIndices.includes(index),
  );

  return {data: filteredData, fields: filteredFields};
}

/**
 * Function to filter initial visible columns based on user input. Will filter according to original fields.
 * If no columns are specified, all original columns will be visible.
 * @param originalResults Original results from parsed CSV file
 * @param visibleColumns String of visible columns (e.g. 'A,C,E')
 * @returns object with filtered fields and their visibility
 */
export function filterInitialVisibleColumns(originalResults: Results, visibleColumns: string): Record<string, boolean> {
  if (visibleColumns === '') return Object.fromEntries(originalResults.fields.map((field) => [field, true]));

  const visibleColumnIndices = lettersToIndices(visibleColumns);

  const visibleFields = originalResults.fields.filter((_, index) =>
    visibleColumnIndices.includes(index),
  );

  let insideVisibleColumns = Object.fromEntries(
    originalResults.fields.map((field) => [field, false]),
  );

  visibleFields.forEach((field) => {
    insideVisibleColumns[field] = true;
  });

  return insideVisibleColumns;
}
